/**
 * @module Components
 *
 */
import React, { useState, FormEvent, useCallback, useMemo } from "react";
import { useUrlSearch, usePending, useError } from "app/utils/hooks";
import { MessageDescriptor } from "react-intl";
import { useParams, useLocation, Link } from "react-router-dom";
import { AuthService } from "app/services";
import { UserModel } from "app/models";
import { cls } from "app/utils";
import { PreviewFile } from "app/utils/file";
import FormItem from "app/components/inputs/FormItem";
import PhoneInput from "react-phone-input-2";
import UserAvatarUploader from "app/components/UserAvatarUploader";
import { AgeConsent, TermsOfService } from "app/components/inputs/CheckBox";
import { validateUser } from "./validate";
import styles from "./styles.module.scss";
import { Trans, useTranslation } from "react-i18next";
import { getUTMParams } from "app/utils/hooks/getUTMParams";
import {
  trackSignupFieldError,
  trackSignupFormStarted,
} from "app/utils/analytics-helpers/signupFunnel";
import LOCALES from "./locale";
import {
  apiFieldErrors,
  FieldError,
  formFieldName,
  validationFieldErrors,
} from "./fieldErrors";

type PropsDef = {
  user: Partial<UserModel>;
  verificationToken: string;
  phoneCountry: string;
  phoneNormalized: string;
  onUserChange: (user: Partial<UserModel>) => void;
  onConsent: (user: Partial<UserModel>) => void;
  onSignin: (user: Partial<UserModel>) => void;
  onDone: (data: { user: UserModel; provider?: string }) => void;
  consentToken?: string;
  lockEmail?: boolean;
  hideOutsideSources?: boolean;
  coachSignup?: boolean;
  coachId?: string;
  phoneRequired?: boolean;
  // Sends the signup funnel events (CN-2742). Off for invite links.
  inFunnel?: boolean;
};

type FormErrors = {
  firstName?: string | MessageDescriptor;
  lastName?: string | MessageDescriptor;
  email?: string | MessageDescriptor;
  phone?: string | MessageDescriptor;
  password?: string | MessageDescriptor;
  ageConsent?: string | MessageDescriptor | null;
  tosConsent?: string | MessageDescriptor | null;
  avatar?: string | MessageDescriptor | null;
  facebook?: string | MessageDescriptor | null;
  unknown?: string | MessageDescriptor | null;
};

const initErrors: FormErrors = {};
const initMatch: boolean = false;

// type ProviderType = {
//   data: UserModel,
//   provider: string
// };

const SIGNUP_SOURCE = "self_sign_up";

export default function SignUpForm(props: PropsDef) {
  const { t } = useTranslation();
  const [isMatch, setMatch] = useState(initMatch);
  const [pending, startPending, stopPending] = usePending();
  const [errorsForm, addErrors, clearErrors] = useError(initErrors);
  const { team_token, invitation_token } = useParams<{
    team_token: string;
    invitation_token: string;
  }>();
  const [searchParams] = useUrlSearch();
  const location = useLocation();
  const isPhoneInvite = props.phoneNormalized !== null;
  const [phoneData, setPhoneData] = useState({
    phone: "",
    phone_country: props.phoneCountry || "",
    phone_normalized: props.phoneNormalized || "",
  });

  let signinPath: string = "/signin";
  if (team_token != null || invitation_token != null) {
    // invitaions that have accounts will click signin
    // after a successfull signin the redirect should come back to signup
    // this will allow signup to redirect to the proper team or space after signin
    signinPath = `${signinPath}?return_url=${encodeURIComponent(
      location.pathname + location.search,
    )}`;
  } else if (location.search != null && location.search.length) {
    signinPath = `/signin${location.search}`;
  }

  const updateUserState = (userState: Partial<UserModel>) => {
    const user = { ...props.user, ...userState };
    props.onUserChange(user);
  };

  const response_errors: ErrorObjMap = useMemo(
    () => ({
      signup: {
        password_length: {
          id: "password",
          message: {
            id: "error.invalid.password",
            defaultMessage: t(
              "Password must between 6 and 128 characters long.",
            ),
          },
        },
        email_unavailable: {
          id: "email",
          message: {
            id: "error.unavailable.email",
            defaultMessage: t(
              "This email is already in use, please sign in or reset password.",
            ),
          },
        },
        duplicate_email: {
          id: "email",
          message: {
            id: "error.unavailable.email",
            defaultMessage: t(
              "This email is already in use, please sign in or reset password.",
            ),
          },
        },
        email_already_taken: {
          id: "email",
          message: {
            id: "error.unavailable.email",
            defaultMessage: t(
              "This email is already in use, please sign in or reset password.",
            ),
          },
        },
        email_invalid: {
          id: "email",
          message: {
            id: "error.invalid.email",
            defaultMessage: t("The email address entered is not valid."),
          },
        },
        phone_already_exists: {
          id: "phone",
          message: {
            id: "error.unavailable.phone",
            defaultMessage: t("This phone is already in use, please sign in."),
          },
        },
        phone_already_taken: {
          id: "phone",
          message: {
            id: "error.unavailable.phone",
            defaultMessage: t("This phone is already in use, please sign in."),
          },
        },
        unknown: {
          id: "unknown",
          message: {
            id: "error.signup.unknown",
            defaultMessage: t("The account failed to create."),
          },
        },
        no_connection: {
          id: "unknown",
          message: {
            id: "error.signup.no_connection",
            defaultMessage: t(
              "The account failed to create. No internet connection.",
            ),
          },
        },
      },
      facebook: {
        facebook_email_exists: {
          id: "error.signup.facebook.email_exists",
          defaultMessage: t(
            "The email associated with this Facebook account is already in use.",
          ),
        },
        facebook_email_taken: {
          id: "error.signup.facebook.email_taken",
          defaultMessage: t(
            "The email associated with this Facebook account is already in use.",
          ),
        },
        email_not_allowed: {
          id: "error.signup.facebook.email_unavailable",
          defaultMessage: t(
            "The Facebook profile provided either does not have an email or security settings prevents sharing it. Please check your profile and try again.",
          ),
        },
        unknown: {
          id: "error.signup.facebook.unknown",
          defaultMessage: t(
            "Unable to sign up with Facebook. Please try again.",
          ),
        },
      },
    }),
    [],
  );

  const handleApiError = useCallback(
    (errors: string[], addErrors: (e: FormErrors) => void) => {
      const typeErrors = response_errors["signup"];

      if (errors == null) {
        errors = ["unknown"];
      }

      const formErrors: FormErrors = Object.assign(
        {},
        ...errors.map((key: string) => {
          const err = typeErrors[key];
          if (err && (err as EnhancedMessageDescriptor).message) {
            return { [err.id]: (err as EnhancedMessageDescriptor).message };
          } else if (err) {
            return err as MessageDescriptor;
          }
          return {};
        }),
      );

      addErrors(formErrors as FormErrors);
    },
    [],
  );

  const handlePhoneChange = (value: string, data: any) => {
    const dialCode = data.dialCode || "";
    const countryCode = data.countryCode?.toUpperCase() || "US";

    const localPart = value.startsWith(dialCode)
      ? value.slice(dialCode.length)
      : value;

    setPhoneData({
      phone: localPart,
      phone_country: countryCode,
      phone_normalized: `+${dialCode}${localPart}`,
    });
  };

  const utmParams = getUTMParams();

  const reportFieldErrors = (fieldErrors: FieldError[]) => {
    if (props.inFunnel) {
      fieldErrors.forEach(({ field, type }) => trackSignupFieldError(field, type));
    }
  };

  // Counts fields only: focusing the submit button or a link first is not
  // starting the form. The visible phone input has no name of its own.
  const reportFormStarted = (e: React.FocusEvent<HTMLFormElement>) => {
    const input = e.target as Element;
    if (!props.inFunnel || !["INPUT", "SELECT", "TEXTAREA"].includes(input.tagName)) {
      return;
    }
    const name =
      input.getAttribute("type") === "tel" ? "phone" : input.getAttribute("name") || "";
    trackSignupFormStarted(formFieldName(name));
  };

  const validatePhoneInput = (): boolean => {
    if (!phoneData.phone || phoneData.phone.length <= 2) {
      addErrors({ phone: { ...LOCALES.send_confirmation_title } });
      reportFieldErrors([{ field: "phone", type: "required" }]);
      return false;
    }
    return true;
  };

  const handleSignup = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    clearErrors();
    const form = getFormElements(e);
    const user = getUserFromForm(form, props);
    const validation = validateUser(user, isPhoneInvite);
    if (searchParams.coach_id) {
      user["coachId"] = searchParams.coach_id;
    }

    if (!validation.isValid) {
      addErrors(validation.errors);
      reportFieldErrors(validationFieldErrors(validation.errors, user));
      if (props.phoneRequired && !validatePhoneInput()) {
        return;
      }
      return;
    }

    if (props.phoneRequired && !validatePhoneInput()) {
      return;
    }
    if (!user.ageConsent && !user.consentToken) {
      props.onConsent(user);

      return;
    }

    startPending();
    const authAny = (AuthService as any).signUp(
      user,
      user.tosConsent,
      utmParams,
      props.verificationToken,
    );

    authAny
      .then((user: UserModel) => {
        props.onDone({ user });
      })
      .catch((e: any) => {
        handleApiError(e.errors, addErrors);
        reportFieldErrors(apiFieldErrors(e?.errors, user));
      })
      .finally(stopPending);
  };

  // const handleFacebookSignup = () => {
  //   clearErrors();
  //   const { tosConsent } = props.user;
  //   const validation = validateProperty('tosConsent', tosConsent);
  //   if (!validation.isValid) {
  //     addErrors({ tosConsent: validation.error });
  //     return;
  //   }
  //
  //   const signinIfEmailExists = (error: string) => {
  //     if (error === 'facebook_email_exists' || error === 'facebook_email_taken') {
  //       return (AuthService as any).authenticateWithFacebook()
  //         .then((user: UserModel) => props.onSignin(user));
  //     } else {
  //       return handleResponseError('facebook', error, addErrors);
  //     }
  //   };
  //
  //   startPending();
  //   (AuthService as any).signUpWithFacebook(SIGNUP_SOURCE, tosConsent, addUTMParams())
  //     .then((data: ProviderType) => props.onDone({ user: data.data, provider: data.provider }))
  //     .catch(signinIfEmailExists)
  //     .finally(stopPending);
  // };

  const checkMatch = (event: FormEvent<HTMLFormElement>) => {
    const els = getFormElements(event);
    const password = els.password.value.trim();
    const confirmPassword = els.passwordVerify.value.trim();

    const match = password.length > 0 && password === confirmPassword;
    setMatch(match);
  };

  return (
    <div className={styles.signupForm}>
      {props.coachSignup ? (
        <div className={cls(styles.coachSignupHeaderCopy, "mb-1 mt-1")}>
          <Trans
            i18nKey="create_account_free_trial"
            components={{ strong: <strong />, br: <br /> }}
          />
        </div>
      ) : (
        ""
      )}

      <form
        noValidate
        onChange={checkMatch}
        onFocus={reportFormStarted}
        onSubmit={handleSignup}
        target="_self"
      >
        <FormItem error={getError("firstName", errorsForm)}>
          <input
            name="firstName"
            defaultValue={props.user.firstName}
            placeholder={t("First Name")}
            type="text"
            disabled={pending.status}
          />
        </FormItem>

        <FormItem error={getError("lastName", errorsForm)}>
          <input
            name="lastName"
            defaultValue={props.user.lastName}
            placeholder={t("Last Name")}
            type="text"
            disabled={pending.status}
          />
        </FormItem>

        <FormItem error={getError("email", errorsForm)}>
          <input
            name="email"
            defaultValue={props.user.email}
            placeholder={t("Email")}
            type="text"
            disabled={props.lockEmail || pending.status}
          />
        </FormItem>

        <FormItem
          className={cls({
            [styles.hidden]: !props.phoneRequired && !isPhoneInvite,
          })}
          error={getError("phone", errorsForm)}
        >
          <PhoneInput
            country={props.phoneCountry?.toLowerCase() || "us"}
            value={phoneData.phone_normalized}
            countryCodeEditable={false}
            inputStyle={{ width: "100%", height: "42px" }}
            placeholder={"Phone Number"}
            disabled={isPhoneInvite || !props.phoneRequired}
            onChange={handlePhoneChange}
          />

          {/* Hidden inputs to pass values in form submission */}
          <input type="hidden" name="phone" value={phoneData.phone} />
          <input
            type="hidden"
            name="phone_country"
            value={phoneData.phone_country}
          />
          <input
            type="hidden"
            name="phone_normalized"
            value={phoneData.phone_normalized}
          />

          <label className={styles.phone_number_label}>
            {t("Phone Number (so your coach can contact you)")}
          </label>
        </FormItem>
        <FormItem
          className={styles.checkForm}
          error={getError("password", errorsForm)}
        >
          <input
            name="password"
            defaultValue={props.user.password}
            placeholder={t("Password")}
            type="password"
            disabled={pending.status}
          />
          <span className={cls(styles.icon, isMatch ? styles.showing : "")}>
            <i className="fas fa-check-circle"></i>
          </span>
        </FormItem>

        <FormItem className={styles.checkForm}>
          <input
            name="passwordVerify"
            defaultValue={props.user.passwordVerify}
            placeholder={t("Re-type password")}
            type="password"
            disabled={pending.status}
          />
          <span className={cls(styles.icon, isMatch ? styles.showing : "")}>
            <i className="fas fa-check-circle"></i>
          </span>
        </FormItem>

        <FormItem error={getError("avatar", errorsForm)}>
          <UserAvatarUploader
            file={props.user.avatar as PreviewFile}
            onChange={(file: PreviewFile) => updateUserState({ avatar: file })}
            disabled={pending.status}
          />
        </FormItem>

        <FormItem
          className="text-left"
          error={getError("ageConsent", errorsForm)}
        >
          <AgeConsent
            disabled={pending.status}
            checked={props.user.ageConsent}
            onChange={(checked: boolean) =>
              updateUserState({ ageConsent: checked })
            }
          />
        </FormItem>

        <FormItem
          className={cls(styles.coachSignupCopy, "text-left")}
          error={getError("tosConsent", errorsForm)}
        >
          <TermsOfService
            disabled={pending.status}
            checked={props.user.tosConsent}
            onChange={(checked: boolean) =>
              updateUserState({ tosConsent: checked })
            }
          />
        </FormItem>

        {props.coachSignup ? (
          <FormItem className={styles.coachSignupCopy}>
            {t(
              "By creating an account, you consent to receiving emails from us (don’t worry - we hate spam just like you!). You can change your subscription preferences at any time in the footer of our emails.",
            )}
          </FormItem>
        ) : (
          ""
        )}

        <FormItem error={getError("unknown", errorsForm)}>
          <button className="btn btn-primary full" disabled={pending.status}>
            {renderSignupButton(props)}
          </button>
        </FormItem>
      </form>

      <FormItem>
        <Link className="btn btn-link full" to={signinPath}>
          {renderSigninButton(props)}
        </Link>
      </FormItem>
    </div>
  );
}

const renderSigninButton = (props: PropsDef) => {
  const { t } = useTranslation();
  if (props.coachSignup) {
    return t("Already Have an Account? Sign In Here.");
  } else {
    return t("Sign In");
  }
};

const renderSignupButton = (props: PropsDef) => {
  const { t } = useTranslation();
  if (!props.user.ageConsent && !props.user.consentToken) {
    return t("Provide Billing Information");
  } else if (props.coachSignup) {
    return t("Begin My Free Trial");
  } else {
    return t("Sign Up");
  }
};

type EnhancedMessageDescriptor = { id: string; message: MessageDescriptor };

type ErrorObjMap = {
  [key: string]: {
    [key: string]: MessageDescriptor | EnhancedMessageDescriptor;
  };
};

interface FormElements extends HTMLCollection {
  firstName: HTMLInputElement;
  lastName: HTMLInputElement;
  email: HTMLInputElement;
  password: HTMLInputElement;
  passwordVerify: HTMLInputElement;
}

const getFormElements = (e: FormEvent<HTMLFormElement>) => {
  const form = e.currentTarget.elements as FormElements;
  return form;
};

const getUserFromForm = (form: FormElements, props: PropsDef) => {
  const user = Object.assign(
    {},
    {
      consentToken: props.user.consentToken,
      ageConsent: props.user.ageConsent,
      tosConsent: props.user.tosConsent,
      avatar: props.user.avatar,
      kind: props.user.kind,
      kindTitle: props.user.kindTitle,
      sport: props.user.sport,
    },
    {
      source: SIGNUP_SOURCE,
      firstName: getFormValue(form, "firstName"),
      lastName: getFormValue(form, "lastName"),
      email: getFormValue(form, "email"),
      phone: getFormValue(form, "phone"),
      phoneCountry: getFormValue(form, "phone_country"),
      phoneNormalized: getFormValue(form, "phone_normalized"),
      password: getFormValue(form, "password"),
      passwordVerify: getFormValue(form, "passwordVerify"),
    },
  );
  return user;
};

const getError = (key: keyof FormErrors, errorsForm: FormErrors) => {
  if (errorsForm[key]) {
    return errorsForm[key];
  }
  return null;
};

const getFormValue = (form: any, key: string) => {
  const ref = form[key];
  if (ref) {
    return ref.value.trim();
  }
  return "";
};

// type ErrKeys = keyof typeof response_errors;

// function handleResponseError(key: ErrKeys, error: string, addErrors: (e: FormErrors) => void) {
//   addErrors({ [key]: response_errors[key][error] || response_errors[key]['unknown'] });
// }
